import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { selectStudent, selectUsersLoading, selectUsersError, clearStudent, clearError } from "@/features/users/usersSlice";
import { getStudentByIdentifier } from "@/features/users/usersThunks";
import { fetchStudentPurchases } from "@/features/payments/paymentsThunks";
import { selectStudentPurchases, selectStudentPurchasesLoading, selectStudentPurchasesError } from "@/features/payments/paymentsSelectors";
import { clearStudentPurchases } from "@/features/payments/paymentsSlice";
import { PurchaseStatus } from "@/types/payment.types";

interface StudentInfoModalProps {
  open: boolean;
  onClose: () => void;
}

const StudentInfoModal: React.FC<StudentInfoModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const [studentId, setStudentId] = useState("");

  const studentData = useAppSelector(selectStudent);
  const loading = useAppSelector(selectUsersLoading);
  const error = useAppSelector(selectUsersError);

  const studentPurchases = useAppSelector(selectStudentPurchases);
  const purchasesLoading = useAppSelector(selectStudentPurchasesLoading);
  const purchasesError = useAppSelector(selectStudentPurchasesError);

  useEffect(() => {
    // Clear student data and errors when modal opens
    if (open) {
      dispatch(clearStudent());
      dispatch(clearError());
      dispatch(clearStudentPurchases());
      setStudentId("");
    }
  }, [open, dispatch]);

  useEffect(() => {
    // Fetch purchases when student is found
    if (studentData?.identifier) {
      dispatch(fetchStudentPurchases(studentData.identifier));
    }
  }, [studentData, dispatch]);

  const handleSearch = async () => {
    if (!studentId.trim()) {
      return;
    }
    await dispatch(getStudentByIdentifier(studentId.trim()));
};

  const handleClose = () => {
    setStudentId("");
    dispatch(clearStudent());
    dispatch(clearError());
    dispatch(clearStudentPurchases());
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const getStatusColor = (status: PurchaseStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case PurchaseStatus.PENDING:
        return 'warning';
      case PurchaseStatus.COMPLETED:
      case PurchaseStatus.APPROVED:
        return 'success';
      case PurchaseStatus.FAILED:
      case PurchaseStatus.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: PurchaseStatus): string => {
    switch (status) {
      case PurchaseStatus.PENDING:
        return 'Pendiente';
      case PurchaseStatus.COMPLETED:
        return 'Completado';
      case PurchaseStatus.APPROVED:
        return 'Aprobado';
      case PurchaseStatus.FAILED:
        return 'Fallido';
      case PurchaseStatus.REJECTED:
        return 'Rechazado';
      default:
        return status;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: { xs: "100%", md: "60%" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SchoolIcon color="primary" />
          <Typography variant="h6" component="span">
            Consultar Información de Pago de Estudiante
          </Typography>
        </Box>
        <Button
          onClick={handleClose}
          size="small"
          sx={{ minWidth: "auto", p: 0.5 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Search Section */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              placeholder="Ingrese el ID del estudiante"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              size="medium"
              autoFocus
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !studentId.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ minWidth: 120, height: 56 }}
            >
              {loading ? "Buscando..." : "Buscar"}
            </Button>
          </Box>

          {/* Error Message */}
          {error && (
            <Alert severity="error" onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}

          {/* Student Data Display */}
          {studentData && (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                p: 3,
                backgroundColor: "background.default",
              }}
            >
              <Stack spacing={2.5}>
                {/* Header with Name and Status */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <PersonIcon color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {studentData.displayName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {studentData.identifier} | AppID: {studentData.id}
                      </Typography>
                    </Box>
                  </Box>
                  {studentData.suspended !== undefined && (
                    <Chip
                      label={studentData.suspended ? "Suspendido" : "Activo"}
                      color={studentData.suspended ? "error" : "success"}
                      size="small"
                    />
                  )}
                </Box>

                <Divider />

                {/* Student Details */}
                <Stack spacing={2}>
                  {studentData.firstName && studentData.lastName && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <PersonIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Nombre Completo
                        </Typography>
                        <Typography variant="body1">
                          {studentData.firstName} {studentData.lastName}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <EmailIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Correo Electrónico
                      </Typography>
                      <Typography variant="body1">{studentData.email}</Typography>
                    </Box>
                  </Box>

                  {studentData.phone && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <PhoneIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Teléfono
                        </Typography>
                        <Typography variant="body1">{studentData.phone}</Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>

                {/* Metadata */}
                {(studentData.createdAt || studentData.updatedAt) && (
                  <>
                    <Divider />
                    <Stack spacing={1}>
                      {studentData.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          Creado: {new Date(studentData.createdAt).toLocaleString("es-CO")}
                        </Typography>
                      )}
                      {studentData.updatedAt && (
                        <Typography variant="caption" color="text.secondary">
                          Actualizado: {new Date(studentData.updatedAt).toLocaleString("es-CO")}
                        </Typography>
                      )}
                    </Stack>
                  </>
                )}
              </Stack>
            </Box>
          )}

          {/* Student Purchases Section */}
          {studentData && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <ReceiptIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Historial de Pagos
                </Typography>
              </Box>

              {purchasesLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              )}

              {purchasesError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {purchasesError}
                </Alert>
              )}

              {!purchasesLoading && !purchasesError && studentPurchases.length === 0 && (
                <Alert severity="info">
                  No se encontraron compras para este estudiante.
                </Alert>
              )}

              {!purchasesLoading && studentPurchases.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Fecha</strong></TableCell>
                        <TableCell><strong>Referencia</strong></TableCell>
                        <TableCell><strong>Concepto</strong></TableCell>
                        <TableCell align="right"><strong>Monto</strong></TableCell>
                        <TableCell align="center"><strong>Estado</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {studentPurchases.map((purchase) => (
                        <TableRow key={purchase.id} hover>
                          <TableCell>
                            {new Date(purchase.createdAt).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>{purchase.referenceCode}</TableCell>
                          <TableCell>
                            {purchase.concepts.map(c => c.name).join(', ')}
                          </TableCell>
                          <TableCell align="right">
                            ${purchase.concepts.reduce((sum, c) => sum + c.amount, 0).toLocaleString()}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={getStatusLabel(purchase.status)}
                              color={getStatusColor(purchase.status)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentInfoModal;
